import { create } from "zustand";
import { sdk } from "@/lib/medusa";
import { 
  fetchNotifications, 
  markNotificationAsRead, 
  markAllNotificationsAsRead, 
  getNotificationToken 
} from "@/modules/notifications/actions";
import type { AppNotification } from "@/modules/notifications/types";

interface NotificationsState {
  notifications: AppNotification[];
  unreadCount: number;
  isConnecting: boolean;
  abortController: (() => void) | null;
  offset: number;
  limit: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  
  // Actions
  initialize: (customerId: string) => Promise<void>;
  loadMore: () => Promise<void>;
  cleanup: () => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
}

export const useNotificationsStore = create<NotificationsState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isConnecting: false,
  abortController: null,
  offset: 0,
  limit: 10,
  hasMore: true,
  isLoadingMore: false,

  initialize: async () => {
    const state = get();
    
    // Si ya estamos conectados o intentando conectar para este usuario, no hacemos nada.
    if (state.isConnecting || state.abortController) return;

    set({ isConnecting: true, offset: 0 });

    try {
      // 1. Cargar Historial inicial (primeros 10)
      const data = await fetchNotifications(0, state.limit);
      if (data?.notifications) {
        set({
          notifications: data.notifications,
          unreadCount: data.notifications.filter((n: AppNotification) => !n.read_at).length,
          hasMore: data.count > data.notifications.length,
          offset: data.notifications.length
        });
      }

      // 2. Conectar al SSE de Medusa v2
      const authorization = await getNotificationToken();
      
      // Verificación de seguridad: si cleanup() fue llamado mientras esperábamos el token
      if (!get().isConnecting) return;

      const { stream, abort } = await sdk.client.fetchStream("/store/notifications/stream", {
        headers: { authorization }
      });

      set({ abortController: abort });

      if (!stream) {
        set({ isConnecting: false });
        return;
      }

      // 3. Leer el stream en tiempo real
      for await (const chunk of stream) {
        if (!chunk.data) continue;
          const parsedData = JSON.parse(chunk.data);
          
          // Ignoramos mensajes de latido (keep-alive)
          if (parsedData.type === "connected") continue;

          // Añadimos la nueva notificación al estado global
          set((prev) => {
            // Protección contra duplicados en la red
            if (prev.notifications.some(n => n.id === parsedData.id)) return prev;
            return {
              notifications: [parsedData, ...prev.notifications],
              unreadCount: prev.unreadCount + 1,
              offset: prev.offset + 1 // Ajustamos el offset para que el loadMore no traiga repetidos
            };
          });
        
      }
    } catch (e) {
      console.error("[SSE] Conexión perdida o rechazada:", e);
    } finally {
      set({ isConnecting: false, abortController: null });
    }
  },

  loadMore: async () => {
    const state = get();
    // Si ya estamos cargando o no hay más en BD, cancelamos
    if (state.isLoadingMore || !state.hasMore) return;

    set({ isLoadingMore: true });

    try {
      const data = await fetchNotifications(state.offset, state.limit);
      if (data?.notifications && data.notifications.length > 0) {
        set((prev) => ({
          notifications: [...prev.notifications, ...data.notifications],
          offset: prev.offset + data.notifications.length,
          hasMore: data.count > prev.offset + data.notifications.length
        }));
      } else {
        set({ hasMore: false });
      }
    } catch (err) {
      console.error("Error cargando más notificaciones:", err);
    } finally {
      set({ isLoadingMore: false });
    }
  },

  cleanup: () => {
    const { abortController } = get();
    if (abortController) {
      abortController(); // Corta el túnel HTTP activo
    }
    
    // Purga la memoria RAM del estado global
    set({
      notifications: [],
      unreadCount: 0,
      isConnecting: false,
      abortController: null,
      offset: 0,
      hasMore: true,
      isLoadingMore: false
    });
  },

  markAsRead: (id: string) => {
    set((prev) => ({
      notifications: prev.notifications.map((n) => 
        n.id === id ? { ...n, read_at: new Date().toISOString() } : n
      ),
      unreadCount: Math.max(0, prev.unreadCount - 1)
    }));
    
    // Llamada silenciosa al servidor
    markNotificationAsRead(id).catch(console.error);
  },

  markAllAsRead: () => {
    set((prev) => ({
      notifications: prev.notifications.map((n) => ({ 
        ...n, 
        read_at: n.read_at || new Date().toISOString() 
      })),
      unreadCount: 0
    }));

    // Llamada silenciosa al servidor
    markAllNotificationsAsRead().catch(console.error);
  }
}));
